import { Check, X } from 'lucide-react';
import { Button } from '@eo/shared';

import { AssigneeChip } from './AssigneeChip';

import { cardRail } from './helpers';
import type { TaskCardProps } from './types';

// A card is scanned and decided on, never filled in: the commitment and whose
// client it serves. Its context lives in the editor, where it gets corrected.
// The right rail holds how big it is, giving way to the two verbs when the
// pointer arrives.
export function TaskCard({
  draft,
  eyebrow,
  busy,
  selected,
  disabled,
  onDecide,
  onEdit,
  onSelect,
}: TaskCardProps) {
  const rail = cardRail(draft);

  return (
    <div
      onMouseDown={onSelect}
      onClick={onEdit}
      data-selected={selected}
      className={`group flex cursor-pointer items-start gap-4 rounded-16 border bg-[var(--color-canvas-base)] p-3 transition-colors hover:bg-chip-hover ${
        selected ? 'border-focus' : 'border-border-faint'
      } ${busy ? 'opacity-60' : ''}`}
    >
      <div className="min-w-0 flex-1">
        {eyebrow && (
          <p className="mb-1 text-[10px] uppercase leading-3 tracking-wide text-text-tertiary">
            {eyebrow}
          </p>
        )}
        <button
          type="button"
          onClick={onEdit}
          className="rounded-8 text-left text-base leading-5 text-text-primary"
        >
          {draft.taskName || 'Untitled task'}
        </button>
        {draft.client && (
          <p className="mt-1.5 text-xs leading-[14px] text-text-tertiary">{draft.client}</p>
        )}
      </div>

      {/* The rail and the verbs occupy the same slot: one gives way to the
          other so reaching for a card never changes its height or width. */}
      <div role="presentation" onClick={(e) => e.stopPropagation()} className="relative shrink-0">
        <div className="flex items-center gap-1.5 px-2 transition-opacity group-hover:opacity-0 group-focus-within:opacity-0">
          {rail.estimate && (
            <span className="text-xs leading-[18px] text-text-tertiary">{rail.estimate}</span>
          )}
          {rail.people.map(({ name, color }) => (
            <AssigneeChip key={name} name={name} color={color} />
          ))}
          {rail.overflow > 0 && <AssigneeChip name={`+${rail.overflow}`} color={null} />}
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <Button
            variant="ghost"
            size="icon"
            title="Approve (Enter)"
            aria-label="Approve"
            disabled={disabled || busy}
            onClick={() => onDecide('approve')}
            className="text-text-tertiary hover:text-success"
          >
            <Check className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title="Reject (Delete)"
            aria-label="Reject"
            disabled={disabled || busy}
            onClick={() => onDecide('reject')}
            className="text-text-tertiary hover:text-button-destructive-foreground"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
